import { z } from 'zod';
import type { RfqTemplateField } from './rfq.types';

/**
 * The forms' rules -- one Zod schema per wizard step and per dialog -- mirroring the platform's validation
 * so a refusal is caught before the round trip. The platform still decides; these only save a request and
 * name the field in place.
 */

const required = (label: string) => z.string().trim().min(1, `${label} is required.`);

/**
 * What the template decides on Step 1: which inputs are required (each system field's Mandatory switch) and what
 * each is called. An optional input left blank is filled by the platform: the title with the BID number, the start
 * with now, the deadline seven days after the start, the category with the template's.
 */
export interface DetailsRules {
  titleRequired?: boolean;
  startRequired?: boolean;
  deadlineRequired?: boolean;
  categoryRequired?: boolean;
  descriptionRequired?: boolean;
  budgetRequired?: boolean;
  labels?: Partial<
    Record<'title' | 'bidStart' | 'bidDeadline' | 'category' | 'description' | 'budgetReference', string>
  >;
}

const optionalText = () => z.string().trim();

/** BID Details' Description and a location's Delivery Instructions: at most 500 characters (2026-10-14). */
export const LONG_TEXT_MAX = 500;
/** "<field> must not exceed 500 characters.", named as the field's heading reads. */
const tooLong = (label: string) => `${label} must not exceed ${String(LONG_TEXT_MAX)} characters.`;
/** BID Details' BID Title and Budget Reference: 5 to 100 characters when given (2026-10-22). */
export const SHORT_TEXT_MIN = 5;
export const SHORT_TEXT_MAX = 100;
/** "<field> must not exceed <n> characters.", named as the field's heading reads. */
const tooLongBy = (label: string, max: number) => `${label} must not exceed ${String(max)} characters.`;
/**
 * A short text of `SHORT_TEXT_MIN`..`SHORT_TEXT_MAX` characters, counted trimmed. Blank passes here: whether it may be
 * blank is the required rule's, so a required blank one reads "is required." and not "at least 5".
 */
const shortText = (base: z.ZodString, label: string) =>
  base
    .refine((value) => value === '' || value.length >= SHORT_TEXT_MIN, `${label} must be at least ${String(SHORT_TEXT_MIN)} characters.`)
    .refine((value) => value.length <= SHORT_TEXT_MAX, tooLongBy(label, SHORT_TEXT_MAX));

/** Step 1 -- BID Details. Start not in the past, deadline after start, as the platform requires. */
export function detailsSchema(
  today: string,
  rules: DetailsRules = { titleRequired: true, startRequired: true, deadlineRequired: true, categoryRequired: true },
) {
  const label = {
    title: 'BID Title',
    bidStart: 'BID Start Date',
    bidDeadline: 'BID Deadline',
    category: 'Category',
    description: 'Description',
    budgetReference: 'Budget Reference',
    ...rules.labels,
  };
  return z
    .object({
      /* 5 to 100 characters (2026-10-22), as Budget Reference. */
      title: shortText(rules.titleRequired ? required(label.title) : optionalText(), label.title),
      bidStart: (rules.startRequired ? required(label.bidStart) : optionalText()).refine(
        (value) => !value.trim() || value >= today,
        `${label.bidStart} cannot be in the past.`,
      ),
      bidDeadline: rules.deadlineRequired ? required(label.bidDeadline) : optionalText(),
      category: rules.categoryRequired ? required(label.category) : optionalText(),
      /* Drawn when the template has its Budget Reference field; otherwise kept so a stored value survives a save. */
      budgetReference: shortText(
        rules.budgetRequired ? required(label.budgetReference) : optionalText(),
        label.budgetReference,
      ),
      /* Free text, capped at 500 as the platform caps it. */
      description: (rules.descriptionRequired ? required(label.description) : z.string()).max(
        LONG_TEXT_MAX,
        tooLong(label.description),
      ),
    })
    .refine((values) => !values.bidStart || !values.bidDeadline || values.bidDeadline > values.bidStart, {
      path: ['bidDeadline'],
      message: 'Bid Deadline must be after the Start Date.',
    });
}

export type DetailsFormValues = z.infer<ReturnType<typeof detailsSchema>>;

/** Step 2 -- the products grid: at least one line. */
export const productsStepSchema = z.object({
  lineCount: z.number().int().min(1, 'Add at least one product to continue.'),
});

/** Step 3 -- at least one supplier. */
export const suppliersStepSchema = z.object({
  supplierIds: z.array(z.number()).min(1, 'Select at least one supplier to continue.'),
});

/** Step 5 -- the acknowledgement. */
export const declarationStepSchema = z.object({
  accepted: z.literal(true, { errorMap: () => ({ message: 'You must acknowledge the declaration to continue.' }) }),
});

/** One delivery location -- the single form and each of the multiple ones. */
/** `instructionsRequired`: the template switched its Delivery Instructions field Mandatory. */
/**
 * Which delivery inputs the template makes required (each system field's Mandatory switch; a field the template lacks
 * stays required, instructions excepted). An optional one left blank is filled by the platform.
 */
export interface LocationRules {
  name?: boolean;
  contact?: boolean;
  address?: boolean;
  instructions?: boolean;
  /** What the dialog heads Delivery Instructions with (the template's label), which its messages name. */
  instructionsLabel?: string;
  /** The contact's field name (the template's label, "Contact Number"), which its 10-digit message names. */
  contactLabel?: string;
}

/** A delivery contact number's digits (2026-10-22): exactly ten, digits only. */
export const CONTACT_DIGITS = 10;

/** "<label> must be a 10-digit number." */
export const contactNumberMessage = (label: string) => `${label} must be a ${String(CONTACT_DIGITS)}-digit number.`;

/** What a contact number input keeps of what is typed or pasted: its digits, at most ten. */
export function contactDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, CONTACT_DIGITS);
}

const ALL_LOCATION_RULES: LocationRules = { name: true, contact: true, address: true, instructions: false };

export function locationSchema(bidStart = '', rules: LocationRules = ALL_LOCATION_RULES) {
  const text = (needed: boolean | undefined, label: string) => (needed ? required(label) : z.string().trim());
  return z.object({
    name: text(rules.name, 'Location').max(150, 'Location must be 150 characters or fewer.'),
    /* A 10-digit number when given (2026-10-22); required exactly when the template's Contact Number is Mandatory. */
    contact: text(rules.contact, 'Contact').refine(
      (value) => value === '' || (/^\d+$/.test(value) && value.length === CONTACT_DIGITS),
      contactNumberMessage(rules.contactLabel ?? 'Contact Number'),
    ),
    /* No longer asked for: a location takes the BID deadline. A date stored before is kept as it was. */
    deliveryDate: z.string().trim().refine(
      (value) => !bidStart || !value.trim() || value >= bidStart,
      'Delivery Date must be on or after the Start Date.',
    ),
    address: text(rules.address, 'Address'),
    instructions: text(rules.instructions, rules.instructionsLabel ?? 'Delivery Instructions').max(
      LONG_TEXT_MAX,
      tooLong(rules.instructionsLabel ?? 'Delivery Instructions'),
    ),
  });
}

export type LocationFormValues = z.infer<ReturnType<typeof locationSchema>>;

/**
 * A location field's message as drawn under its input: "is required." names the field by the label the form shows it
 * under (the template's, e.g. "Contact Number is required."), not the schema's short name; any other message as is.
 */
/** A heading's field name alone: "Delivery Instructions (For Selected Product)" reads "Delivery Instructions". */
export function fieldNameOf(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*$/, '').trim() || label.trim();
}

export function requiredMessage(message: string | undefined, label: string): string | undefined {
  return message?.endsWith('is required.') ? `${label} is required.` : message;
}

/** Force Close: a reason is required, up to 500 characters. */
export const forceCloseSchema = z.object({
  reason: required('Reason').max(500, 'Reason must be 500 characters or fewer.'),
});

export type ForceCloseValues = z.infer<typeof forceCloseSchema>;

/** A supplier's withdrawal: the platform requires the reason, up to 500 characters. */
export const withdrawSchema = forceCloseSchema;
export type WithdrawValues = ForceCloseValues;

/** At most this many CC addresses on a resent invitation. */
export const MAX_RESEND_CC = 10;

const EMAIL = z.string().email();

/** The CC field's addresses: comma-separated, blanks dropped. */
export function ccList(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

/**
 * Resend Invitation (the BID Overview, 2026-10-22): the supplier's e-mail, required and valid; CC optional,
 * comma-separated, each a valid address, at most ten.
 */
export const resendInvitationSchema = z.object({
  email: z.string().trim().min(1, 'Supplier Email is required.').email('Enter a valid email address.'),
  cc: z
    .string()
    .refine((value) => ccList(value).every((entry) => EMAIL.safeParse(entry).success), {
      message: 'Enter valid email addresses, separated by commas.',
    })
    .refine((value) => ccList(value).length <= MAX_RESEND_CC, {
      message: `Add at most ${String(MAX_RESEND_CC)} CC addresses.`,
    }),
});
export type ResendInvitationValues = z.infer<typeof resendInvitationSchema>;

/**
 * Extend the bid deadline: a date later than the current deadline and not in the past; the reason is
 * required (2026-10-27), up to 500 characters -- the same rule as Force Close and Withdraw.
 */
export function extendDeadlineSchema(currentDeadline: string, today: string) {
  return z.object({
    newDeadline: required('New deadline')
      .refine((value) => !currentDeadline || !value.trim() || value > currentDeadline, 'The new deadline must be after the current one.')
      .refine((value) => !value.trim() || value >= today, 'The new deadline cannot be in the past.'),
    reason: forceCloseSchema.shape.reason,
  });
}

export type ExtendDeadlineValues = z.infer<ReturnType<typeof extendDeadlineSchema>>;

/** An approval decision. Refer Back and Reject require a remark; Approve does not. ≤1000, as the platform. */
export function remarkSchema(requiredRemark: boolean) {
  return z.object({
    remark: requiredRemark
      ? required('Remark').max(1000, 'Remark must be 1000 characters or fewer.')
      : z.string().trim().max(1000, 'Remark must be 1000 characters or fewer.'),
  });
}

export type RemarkValues = { remark: string };

/* -- template custom fields ------------------------------------------------------ */

/** The form key a custom field's value is held under (`f<templateFieldId>`). */
export const customFieldKey = (field: RfqTemplateField) => `f${String(field.id)}`;

const NUMERIC = /^-?\d+(\.\d+)?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/*
  The Purchase Request form's rules for a template field's data type (templates' `templateFormSchema`: `ruleForField`
  and its typed lengths), spelled here for the wizard's string-held values -- that schema holds a Number as a number and
  a Checkbox as a boolean, so it cannot validate these values as they are.
*/
/** The builder's "Text -- Up to 100 characters" and "Long text -- Up to 500 characters". */
const TYPED_TEXT_MAX: Readonly<Record<string, number>> = {
  text: 100,
  'short text': 100,
  'masked text': 100,
  'long text': 500,
  'address block': 500,
};
/** The builder's "Number -- Up to 12 digits"; the sign and the decimal point are not digits, nor a lone leading 0. */
export const NUMBER_MAX_DIGITS = 12;
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_FORMAT = /^[0-9+\-() ]*$/;
const CHOICE_TYPES: ReadonlySet<string> = new Set(['dropdown', 'search and select', 'radio']);

function digitCountOf(text: string): number {
  return text.replace(/[^\d.]/g, '').replace(/^0(?=\.)/, '').replace(/\./g, '').length;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** What is wrong with a non-blank answer by the field's data type, as the Purchase Request form says it; null if fine. */
function typedProblem(field: RfqTemplateField, type: string, value: string): string | null {
  const max = TYPED_TEXT_MAX[type];
  if (max !== undefined && value.length > max) return `${field.label} must be ${String(max)} characters or fewer.`;
  switch (type) {
    case 'number':
      if (!NUMERIC.test(value)) return `${field.label} must be a number.`;
      return digitCountOf(value) > NUMBER_MAX_DIGITS
        ? `${field.label} must be at most ${String(NUMBER_MAX_DIGITS)} digits.`
        : null;
    case 'currency':
      return NUMERIC.test(value) ? null : `${field.label} must be a number.`;
    case 'date':
      return ISO_DATE.test(value.slice(0, 10)) && !Number.isNaN(Date.parse(value.slice(0, 10)))
        ? null
        : 'Enter a valid date.';
    case 'email':
      return EMAIL_FORMAT.test(value) ? null : 'Enter a valid email address.';
    case 'phone':
      return PHONE_FORMAT.test(value) ? null : 'Enter a valid phone number.';
    case 'url':
      return isHttpUrl(value) ? null : 'Enter a valid URL starting with http:// or https://.';
    case 'checkbox':
    case 'toggle':
      return value === 'true' || value === 'false' ? null : `${field.label} must be Yes or No.`;
    default: {
      /* A choice field with options configured takes one of them; without, it is free text. */
      const options = (field.options ?? []).map((option) => option.trim()).filter((option) => option !== '');
      return CHOICE_TYPES.has(type) && options.length > 0 && !options.includes(value)
        ? `Choose one of the options for ${field.label}.`
        : null;
    }
  }
}

export interface CustomFieldsSchemaOptions {
  /**
   * Holds each answer to the rules the Purchase Request form applies to its data type (2026-10-27): Text up to 100
   * characters, Long text up to 500, a Number up to 12 digits, a valid date / email / phone / URL, a choice among its
   * options. Without it only what the platform itself refuses is checked (a number, a date).
   */
  typed?: boolean;
}

/**
 * The custom fields of one step, each honouring its data type and mandatory flag, as the platform checks
 * them (`RfqDraftService.typeProblem`; mandatory ones at submit) -- and, `typed`, as a Purchase Request does.
 */
export function customFieldsSchema(
  fields: readonly RfqTemplateField[],
  nameOf: (field: RfqTemplateField) => string = customFieldKey,
  options: CustomFieldsSchemaOptions = {},
) {
  const shape: Record<string, z.ZodTypeAny> = {};
  fields.forEach((field) => {
    const type = (field.fieldType ?? '').toLowerCase();
    shape[nameOf(field)] = z.string().superRefine((raw, ctx) => {
      const value = raw.trim();
      const empty = value === '' || (type === 'checkbox' && value !== 'true');
      if (empty) {
        if (field.mandatory) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${field.label} is required.` });
        return;
      }
      if (options.typed) {
        /* A file is the stored document's id (or the file waiting for the first save): nothing to check by type. */
        if (type === 'file upload') return;
        const problem = typedProblem(field, type, value);
        if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
        return;
      }
      if ((type === 'number' || type === 'currency') && !NUMERIC.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${field.label} must be a number.` });
      }
      if (type === 'date' && !ISO_DATE.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${field.label} must be a date.` });
      }
    });
  });
  return z.object(shape);
}

export type CustomFieldValues = Record<string, string>;
